import "./agGridSetup";
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BUILD_VERSION } from './version';

const BUILD_KEY = 'adroit_build';
const isIPv4Host = (hostname: string): boolean => /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname);
const safeLocalGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeLocalSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage write failures; the app can still boot with defaults.
  }
};
const safeLocalRemove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures.
  }
};
const safeSessionRemove = (key: string) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures.
  }
};

const renderFatalOverlay = (message: string) => {
  const rootElement = document.getElementById('root');
  if (!rootElement) return;
  rootElement.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#f8fafc;padding:24px;font-family:system-ui,-apple-system,BlinkMacSystemFont,sans-serif;">
      <div style="max-width:540px;background:#ffffff;border:1px solid rgba(15,23,42,0.08);border-radius:20px;box-shadow:0 18px 48px rgba(15,23,42,0.08);padding:28px 24px;text-align:center;">
        <div style="width:52px;height:52px;margin:0 auto 16px;border-radius:14px;background:rgba(239,68,68,0.08);color:#dc2626;display:flex;align-items:center;justify-content:center;font-size:24px;font-weight:700;">!</div>
        <h1 style="margin:0 0 8px;font-size:22px;color:#0f172a;">Adroit hit a startup problem</h1>
        <p style="margin:0 0 12px;font-size:14px;line-height:1.5;color:#475569;">We cleared the unsafe client state we could. Please refresh once more. If it still fails, this message will help us pinpoint the remaining issue.</p>
        <code style="display:block;overflow:auto;padding:10px 12px;border-radius:10px;background:#f8fafc;color:#b91c1c;font-size:12px;text-align:left;white-space:pre-wrap;">${message.replace(/[<&>]/g, (char) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[char] || char))}</code>
      </div>
    </div>
  `;
};

const clearRecoverableClientState = () => {
  const localKeys = [
    'discoverant-sidecar-mode',
    'discoverant_last_path',
    'discoverant_last_project',
    'tenantBranding',
    'discoverant_projects_view',
    'discoverant_security_settings',
    'import:lastProjectId',
    'discoverant_external_trusted_sites',
    'collisionMapViewPreference',
  ];

  localKeys.forEach(safeLocalRemove);

  try {
    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith('targets_cache_') ||
        key.startsWith('patents_cache_') ||
        key.startsWith('share_verified_') ||
        key.startsWith('molecule_grid_columns_') ||
        key.startsWith('project_files_') ||
        key.startsWith('read_files_')
      ) {
        safeLocalRemove(key);
      }
    });
  } catch {
    // Ignore enumeration failures.
  }

  [
    'discoverant_quick_search_last',
    'discoverant_nav_context',
    'workbench_recent_tools',
    'share_briefing_seen',
  ].forEach(safeSessionRemove);
};

const installStartupRecovery = () => {
  const recoveryFlag = 'adroit_startup_recovery_attempted';
  let startupWindowActive = true;
  window.setTimeout(() => {
    startupWindowActive = false;
  }, 15000);

  const handleFailure = (reason: unknown) => {
    if (!startupWindowActive) {
      console.error('[startup-recovery]', reason);
      return;
    }
    const message = reason instanceof Error ? reason.message : String(reason || 'Unknown startup error');
    const alreadyTried = (() => {
      try {
        return sessionStorage.getItem(recoveryFlag) === '1';
      } catch {
        return true;
      }
    })();

    if (!alreadyTried) {
      try {
        sessionStorage.setItem(recoveryFlag, '1');
      } catch {
        // Ignore flag persistence failures; fall through to overlay if reload fails.
      }
      clearRecoverableClientState();
      window.location.replace(window.location.pathname + window.location.search + window.location.hash);
      return;
    }

    renderFatalOverlay(message);
  };

  window.addEventListener('error', (event) => {
    handleFailure(event.error || event.message);
  });

  window.addEventListener('unhandledrejection', (event) => {
    handleFailure(event.reason);
  });
};

const resetStaleClientState = () => {
  try {
    const previousBuild = safeLocalGet(BUILD_KEY);
    const hostname = window.location.hostname;

    if (previousBuild !== BUILD_VERSION) {
      clearRecoverableClientState();
      safeLocalSet(BUILD_KEY, BUILD_VERSION);
    }

    if (isIPv4Host(hostname)) {
      safeLocalRemove('tenantBranding');
    }
  } catch {
    // Ignore storage reset failures; the app can still continue with defaults.
  }
};

// Set theme class on body BEFORE React renders to prevent flash of wrong theme
// (ES imports are hoisted, so this runs after imports but before createRoot)
console.log(`ADROIT BUILD: ${BUILD_VERSION}`);
installStartupRecovery();
resetStaleClientState();

const savedTheme = safeLocalGet('theme') || 'light';
document.body.classList.add('theme-' + savedTheme);

// Suppress Ketcher initialization errors in dev mode
if (process.env.NODE_ENV === 'development') {
  const originalError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (String(message).includes('KetcherLogger') ||
        String(message).includes('Target container is not a DOM element')) {
      return true; // Suppress
    }
    return originalError ? originalError(message, source, lineno, colno, error) : false;
  };

  // Also suppress unhandled promise rejections
  window.onunhandledrejection = (event) => {
    if (String(event.reason).includes('KetcherLogger')) {
      event.preventDefault();
      return;
    }
  };
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
